import { MapPin, Phone, Star, ArrowRight } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useState, useEffect } from 'react';

export function HeroSectionSplit() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleBookNow = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen flex flex-col lg:flex-row overflow-hidden">
      {/* Left Side - Content */}
      <div className="w-full lg:w-1/2 bg-navy-900 flex items-center relative overflow-hidden min-h-screen lg:min-h-auto">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-luxury-pattern opacity-10"></div>
        
        <div className="relative z-10 w-full max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-12 sm:py-16 lg:py-20">
          <div className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
          }`}>
            <div className="inline-flex items-center gap-2 glass-luxury px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-6 sm:mb-8">
              <Star className="w-4 h-4 sm:w-5 sm:h-5 text-gold-400 fill-current" />
              <span className="text-xs sm:text-sm font-medium text-white">4.8 Rated • Premium Experience</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 leading-tight">
              <span className="text-white">Hotel A.S.K</span>
              <span className="block gradient-text font-luxury">Grand</span>
            </h1>
            
            <p className="text-base sm:text-lg lg:text-xl text-cream-100 mb-6 sm:mb-8 leading-relaxed">
              Where luxury meets affordability in the heart of Gajuwaka. Experience world-class hospitality just minutes from Vizag Airport.
            </p>

            {/* Key Stats - Mobile Optimized */}
            <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-8 sm:mb-10 py-6 sm:py-8 border-y border-gold-400/20">
              <div className="text-center">
                <div className="text-base sm:text-lg lg:text-xl font-semibold text-gold-400">Premium Rooms</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gold-400 mb-1 sm:mb-2">5km</div>
                <div className="text-xs sm:text-sm text-cream-300">From Airport</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gold-400 mb-1 sm:mb-2">24/7</div>
                <div className="text-xs sm:text-sm text-cream-300">Support</div>
              </div>
            </div>

            {/* CTA Buttons - Mobile Optimized */}
            <div className="flex flex-col gap-3 sm:gap-4 mb-8 sm:mb-10">
              <button
                onClick={handleBookNow}
                className="group flex items-center justify-center gap-3 bg-gold-gradient text-navy-900 px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg hover:shadow-gold transition-all duration-300 transform hover:scale-105 w-full"
              >
                Book Your Stay
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              
              <a
                href="tel:+917799933325"
                className="group flex items-center justify-center gap-3 border-2 border-gold-400/30 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl hover:bg-gold-400/10 hover:border-gold-400 transition-all duration-300 font-semibold text-base sm:text-lg w-full"
              >
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-gold-400" />
                <span className="hidden sm:inline">+91 7799933325</span>
                <span className="sm:hidden">Call Now</span>
              </a>
            </div>

            {/* Location - Mobile Optimized */}
            <div className="flex items-start gap-3 sm:gap-4 text-cream-200">
              <MapPin className="w-5 h-5 sm:w-6 sm:h-6 mt-1 flex-shrink-0 text-gold-400" />
              <div>
                <p className="text-sm sm:text-base font-medium">10-2-9/1, Mehar Nagar</p>
                <p className="text-xs sm:text-sm text-cream-300">Old Gajuwaka, Visakhapatnam, Andhra Pradesh 530026</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Image (Hidden on Mobile) */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <ImageWithFallback
          src="/images/Gajuwaka/Gajuwaka.jpg"
          alt="Hotel A.S.K Grand"
          className="w-full h-full object-cover"
        />
        
        {/* Overlay with Room Info */}
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent"></div>
        
        <div className={`absolute bottom-8 left-8 right-8 transform transition-all duration-1000 delay-500 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <div className="glass-luxury rounded-2xl p-6 border border-gold-400/20">
            <h3 className="text-white font-bold text-xl mb-4">Room Rates</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-cream-200">Suite Room</span>
                <div className="text-right">
                  <span className="text-gold-400 font-bold text-2xl">₹1,750</span>
                  <span className="text-cream-300 text-xs ml-1">per day</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-cream-200">Royal Room</span>
                <div className="text-right">
                  <span className="text-gold-400 font-bold text-2xl">₹1,500</span>
                  <span className="text-cream-300 text-xs ml-1">per day</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-cream-200">Single Room</span>
                <div className="text-right">
                  <span className="text-gold-400 font-bold text-2xl">₹900</span>
                  <span className="text-cream-300 text-xs ml-1">per day</span>
                </div>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gold-400/20">
              <p className="text-xs text-green-400 text-center">✓ Best price guarantee</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Room Rates Card - Only visible on mobile */}
      <div className="lg:hidden bg-navy-800 px-4 py-6">
        <div className="max-w-md mx-auto">
          <h3 className="text-white font-bold text-lg mb-4 text-center">Room Rates</h3>
          
          <div className="space-y-3 mb-4">
            <div className="flex justify-between items-center bg-white/5 rounded-lg p-3">
              <div>
                <span className="text-white font-medium text-sm">Suite Room</span>
                <p className="text-cream-300 text-xs">1-2 Guests</p>
              </div>
              <div className="text-right">
                <div className="text-gold-400 font-bold text-lg">₹1,750</div>
                <div className="text-cream-300 text-xs">per day</div>
              </div>
            </div>
            <div className="flex justify-between items-center bg-white/5 rounded-lg p-3">
              <div>
                <span className="text-white font-medium text-sm">Royal Room</span>
                <p className="text-cream-300 text-xs">1-2 Guests</p>
              </div>
              <div className="text-right">
                <div className="text-gold-400 font-bold text-lg">₹1,500</div>
                <div className="text-cream-300 text-xs">per day</div>
              </div>
            </div>
            <div className="flex justify-between items-center bg-white/5 rounded-lg p-3">
              <div>
                <span className="text-white font-medium text-sm">Single Room</span>
                <p className="text-cream-300 text-xs">1-2 Guests</p>
              </div>
              <div className="text-right">
                <div className="text-gold-400 font-bold text-lg">₹900</div>
                <div className="text-cream-300 text-xs">per day</div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-xs text-green-400">✓ Free cancellation • Best price guarantee</p>
          </div>
        </div>
      </div>
    </section>
  );
}